import type { AggregateClient, EventBatch, WireEvent } from '@celeriant/wire';
import { Conflict, Forbidden, IdempotencyConflict, NotExist, Unauthorized, Unavailable, uuidToWireEventId } from '@celeriant/wire';
import { freeze } from 'immer';
import type { AggregateDef, AnyEventDef } from './aggregate.js';
import { assert } from './assert.js';
import { UpdateRequiredError, type EngineCryptoProvider } from './engine.js';
import {
  checkPrecondition,
  foldEvents,
  initialFrozenState,
  lookupKind,
  validateIncomingEvent,
  UnknownEventKindError,
} from './fold.js';
import { Mutex } from './mutex.js';
import { Notifier, type Listener, type Unsubscribe } from './notifier.js';
import { StatusTracker, type SyncStatus } from './status.js';
import type { Store } from './store.js';
import type { AggregateRecord, DedupRecord, NewEvent, StoredEvent } from './storeTypes.js';
import { awaitPace, pacedUntil } from './writePacing.js';

// The online (OCC) engine for one aggregate instance. The opposite of SyncEngine: no optimistic
// apply, no local writes, no undo, and no stamp comparison. State moves only by folding
// server-confirmed events in server order, so the log's `si` is the aggregate_version and the
// fold order is the read order. `execute()` resolves when the server acks.
//
// The gateway does the ownership verification server-side: 200 when a contested client_seq is
// ours (the ORIGINAL version), 409 idempotency_conflict when a sibling took it, 503 on lag. So
// the loop's only job is the seq discipline the idempotency guide spells out: re-derive on OCC
// conflict, HOLD the seq when the outcome is ambiguous, surface after bounded retries.

const MAX_RETRIES = 3;
// How long a request's event_id stays resolvable to its result. For a fresh execute() this
// window is the whole of the same-key guarantee: the gateway's (client_id, client_seq) check is
// seq-gated (WIRE-CONTRACT, write route), so it fires only when the batch's min client_seq is at
// or below the last seq accepted for this client_id, and a fresh execute() re-derives
// lastSentClientSeq + 1, which sits above that gate. Once this window has expired a same-key
// retry appends and the repeated event_id is never looked at. Entries are rebuilt from the
// stream on catch-up with a server-time-relative lifetime, and pruned on access.
const DEDUP_WINDOW_MS = 90_000;

export interface OnlineEngineOptions<TState> {
  def: AggregateDef<TState, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  aggregateId: string;
  store: Store;
  gateway: AggregateClient;
  clientId: string;
  userId?: string | undefined;
  now?: (() => number) | undefined;
  // A fresh idempotency key (canonical UUID) when the caller supplies none. Injectable so a test
  // gets deterministic ids.
  generateEventId?: (() => string) | undefined;
  // Required iff any kind of the aggregate encrypts. Fresh iv per ATTEMPT on the wire; the local
  // log holds plaintext.
  crypto?: EngineCryptoProvider | undefined;
  onUpdateRequired?: (() => void) | undefined;
  isOnline?: (() => boolean) | undefined;
  // Default true: execute() rejects with OfflineError at entry while isOnline() reports false,
  // rather than spending a doomed catch-up read and a retry ladder on a network that is not
  // there. False restores the try-anyway behaviour.
  failFastWhenOffline?: boolean | undefined;
}

export interface ExecuteResult {
  version: number;
}

// The event's precondition failed against fresh server state: the world changed and the command
// no longer applies. Not retried, because re-deriving cannot help.
export class ValidationError extends Error {
  readonly kind: string;
  constructor(kind: string) {
    super(`online aggregate: precondition failed for "${kind}" against current state`);
    this.name = 'ValidationError';
    this.kind = kind;
  }
}

// Bounded retries exhausted: sustained contention, or a lost ack under replication lag. Carries
// the freshest server version so a UI can show current truth next to the failure.
export class ConflictError extends Error {
  readonly type: string;
  readonly id: string;
  readonly version: number;
  constructor(type: string, id: string, version: number) {
    super(`online aggregate "${type}/${id}": write did not complete after ${MAX_RETRIES} attempts (version ${version})`);
    this.name = 'ConflictError';
    this.type = type;
    this.id = id;
    this.version = version;
  }
}

// The app reports no connectivity at the moment of the call. An online write has no outbox to
// fall back on, so the alternative is a doomed catch-up read plus a retry ladder reaching the
// same failure minutes later with the gateway poked for nothing.
export class OfflineError extends Error {
  readonly type: string;
  readonly id: string;
  constructor(type: string, id: string) {
    super(`online aggregate "${type}/${id}": offline, the write was not attempted`);
    this.name = 'OfflineError';
    this.type = type;
    this.id = id;
  }
}

const BATCHES_PER_CATCHUP_MAX = 100_000;
const EVENTS_PER_CATCHUP_MAX = 1_000_000;

interface PreparedRead {
  readonly rows: NewEvent[];
  readonly dedupPuts: DedupRecord[];
  readonly maxVersion: number;
  readonly maxOwnClientSeq: number;
}

export class OnlineEngine<TState> {
  private readonly def: AggregateDef<TState, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  private readonly type: string;
  private readonly id: string;
  private readonly store: Store;
  private readonly gateway: AggregateClient;
  private readonly clientId: string;
  private readonly userId: string | undefined;
  private readonly now: () => number;
  private readonly generateEventId: () => string;
  private readonly crypto: EngineCryptoProvider | undefined;
  private readonly onUpdateRequired: (() => void) | undefined;
  private readonly isOnline: () => boolean;
  private readonly failFastWhenOffline: boolean;

  private readonly status: StatusTracker;
  private inFlight = 0;
  private lastSyncedAt: number | undefined;
  private lastError: string | undefined;
  // A fold this build cannot complete (unknown kind, newer minor, schema mismatch, a plaintext
  // event of an encrypted kind). The cursor stays put, nothing unfoldable is persisted, and
  // execute() goes read-only rather than emit a write computed against a picture it cannot keep
  // current.
  private updateRequiredFlag = false;

  private readonly notifier: Notifier<TState, unknown>;
  // In-process serialisation of catchUp and execute. Cross-tab serialisation of execute is the
  // Web Lock; this covers the single-instance case and the rest of the engine's state.
  private readonly mutex = new Mutex();
  private record: AggregateRecord;
  private closed = false;
  // The wall clock before which this aggregate's next gateway WRITE must not leave, from the
  // write_backoff_ms the last write response carried. 0 means unpaced. Scope is per engine
  // instance, which under-paces on purpose: the gateway's signal is per gateway, but a
  // process-wide gate would let one hot aggregate stall every other one.
  private writeNotBefore = 0;

  constructor(options: OnlineEngineOptions<TState>) {
    if (options.def.mode !== 'online') {
      throw new Error(`OnlineEngine: aggregate "${options.def.name}" is not mode:'online'`);
    }
    if ('snapshotEvery' in options) throw new Error('OnlineEngine: snapshotEvery is gone; there is no snapshot cadence');
    this.def = options.def;
    this.type = options.def.name;
    this.id = options.aggregateId;
    assert(this.id.length > 0, 'OnlineEngine: aggregateId is set');
    this.store = options.store;
    this.gateway = options.gateway;
    this.clientId = options.clientId;
    assert(this.clientId.length > 0, 'OnlineEngine: clientId is set');
    this.userId = options.userId;
    this.now = options.now ?? Date.now;
    this.generateEventId = options.generateEventId ?? ((): string => crypto.randomUUID());
    this.crypto = options.crypto;
    this.onUpdateRequired = options.onUpdateRequired;
    this.isOnline = options.isOnline ?? ((): boolean => true);
    this.failFastWhenOffline = options.failFastWhenOffline ?? true;
    if (this.encryptsSomething() && this.crypto === undefined) {
      throw new Error(`OnlineEngine: aggregate "${options.def.name}" declares encrypt:true but no crypto provider was supplied`);
    }
    this.status = new StatusTracker(() => ({
      online: this.isOnline(),
      syncing: this.inFlight > 0,
      // Online mode has no unsent local events; there is nothing to be pending.
      pending: 0,
      updateRequired: this.updateRequiredFlag,
      lastSyncedAt: this.lastSyncedAt,
      lastError: this.lastError,
    }));
    this.notifier = new Notifier<TState, unknown>(initialFrozenState(options.def));
    this.record = this.freshRecord();
  }

  get state(): TState { return this.notifier.value; }
  get version(): number { return this.record.serverVersion; }
  get updateRequired(): boolean { return this.updateRequiredFlag; }
  subscribe(listener: Listener<TState, unknown>): Unsubscribe { return this.notifier.subscribe(listener); }
  get statusValue(): SyncStatus { return this.status.value; }
  subscribeStatus(cb: (s: SyncStatus) => void): Unsubscribe { return this.status.subscribe(cb); }
  notifyConnectivityChanged(): void { this.status.emit(); }

  // Hydrates from the state document and folds whatever the log holds above the record's
  // appliedLi (rows the fold left unchanged advanced the record without a state write), then a
  // non-blocking catch-up read. A schema the persisted state does not match is re-folded from
  // the local log, offline, when the log runs from version 1; only a log the server trimmed
  // cannot replay, and then the replica is dropped and re-read from version 0.
  async open(): Promise<void> {
    if (this.closed) throw new Error('OnlineEngine: closed');
    await this.mutex.runExclusive(async () => {
      const loaded = await this.store.unitOfWork(this.type, this.id, 'readonly', async (uow) => {
        const record = await uow.getRecord();
        const state = await uow.getState();
        const stale = (record !== undefined && record.schemaVersion !== this.def.schemaVersion)
          || (state !== undefined && state.schemaVersion !== this.def.schemaVersion);
        const replayable = record === undefined || record.trimFloor === 1;
        const tail = stale
          ? (replayable ? await uow.readTail(0) : [])
          : await uow.readTail(record?.appliedLi ?? 0);
        return { record, state, tail, stale, replayable };
      });
      if (loaded.record !== undefined) this.record = loaded.record;
      if (loaded.stale && !loaded.replayable) {
        await this.dropReplicaLocked();
        return;
      }
      if (loaded.state !== undefined && !loaded.stale) {
        assert(loaded.state.appliedLi <= this.record.appliedLi, 'open: the state document never leads the record');
      }
      const base = loaded.state === undefined || loaded.stale
        ? initialFrozenState(this.def)
        : freeze(loaded.state.state as TState, true);
      const hydrated = this.foldOrLatch(base, loaded.tail);
      this.record = { ...this.record, appliedLi: lastLiOf(loaded.tail, loaded.stale ? 0 : this.record.appliedLi) };
      this.notifier.reset(hydrated);
    });
    if (!this.closed) {
      void this.refresh().catch((e: unknown) => { console.error('OnlineEngine: open catch-up failed:', e); });
    }
  }

  async close(): Promise<void> {
    this.closed = true;
  }

  async refresh(): Promise<void> {
    this.enterSync();
    try {
      await this.mutex.runExclusive(async () => { await this.catchUpLocked(undefined); });
      this.lastSyncedAt = this.now();
      this.lastError = undefined;
    } catch (e: unknown) {
      this.lastError = errorMessage(e);
      throw e;
    } finally {
      this.exitSync();
    }
  }

  // The only write API. Serialised cross-tab by the Web Lock, so two tabs sharing one device
  // keypair (one wire client_id) cannot race a client_seq.
  async execute(kind: string, payload: unknown, opts: { idempotencyKey?: string } = {}): Promise<ExecuteResult> {
    if (this.closed) throw new Error('OnlineEngine: closed');
    if (lookupKind<AnyEventDef<TState>>(this.def.events, kind) === undefined) {
      throw new Error(`online aggregate "${this.type}": unknown event kind "${kind}"`);
    }
    // After the caller-bug guards (those facts do not change when the network comes back) and
    // before any gateway contact, including the catch-up read, which on a dead interface only
    // adds a timeout to a call that cannot succeed. At ENTRY only: once a request is in flight
    // the transport is a better authority than a browser event that is frequently wrong.
    // Outside the enterSync section below, because an offline refusal is expected state, not a
    // sync failure: it must not appear in status.lastError or flicker `syncing`.
    if (this.failFastWhenOffline && !this.isOnline()) throw new OfflineError(this.type, this.id);
    const eventId = opts.idempotencyKey ?? this.generateEventId();
    // event_id crosses the wire as a canonical UUID on write; the dedup index keys on the READ
    // form, so a request's key matches the event_id the gateway echoes on fold. A non-UUID key
    // fails here, at the boundary, rather than deep in the loop.
    const dedupKey = uuidToWireEventId(eventId);
    this.enterSync();
    try {
      const r = await this.withLock(async () => {
        // The last response's backpressure hint is an inter-WRITE gap, not part of the retry
        // ladder, and it must not hold the mutex: refresh() and every SSE-driven catch-up need
        // that lock, and a 2000ms hint holding it would freeze realtime folding to slow down
        // writes the server never asked us to stop reading for. One pace at entry is enough,
        // because writeNotBefore moves only on the success path and that path returns.
        await awaitPace(this.writeNotBefore, this.now);
        if (this.closed) throw new Error('OnlineEngine: closed');
        return this.mutex.runExclusive(() => this.executeLocked(kind, payload, eventId, dedupKey));
      });
      this.lastSyncedAt = this.now();
      this.lastError = undefined;
      return r;
    } catch (e: unknown) {
      // A rejected command and a read-only aggregate have their own channels; a ConflictError or
      // a transport throw is a sync error worth showing.
      if (!(e instanceof ValidationError) && !(e instanceof UpdateRequiredError)) this.lastError = errorMessage(e);
      throw e;
    } finally {
      this.exitSync();
    }
  }

  private async executeLocked(kind: string, payload: unknown, eventId: string, dedupKey: string): Promise<ExecuteResult> {
    const wireType = this.def.wireTypes[kind];
    assert(wireType !== undefined, 'executeLocked: every declared kind has a wire type');
    // A property of the EVENT, not of the call: `occ:false` marks a pure append that contends
    // for nothing, so the guard would only manufacture 409s between peers who never disagreed.
    const useOcc = lookupKind<AnyEventDef<TState>>(this.def.events, kind)?.occ !== false;

    // Read-only once update-required. Checked before AND after the catch-up, because a
    // concurrent refresh or this catch-up itself may latch it.
    this.assertWritable();
    const firstHit = await this.catchUpLocked(dedupKey);
    if (firstHit !== undefined) return { version: firstHit.version };
    this.assertWritable();

    let clientSeq = this.record.lastSentClientSeq + 1;
    let reDerive = false;
    let resumeFromSeq = 0;   // the 409 idempotency_conflict's last_client_seq hint
    let nextDelayMs = 0;     // set by whichever catch decided to retry

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt += 1) {
      if (attempt > 1) {
        await sleep(nextDelayMs);
        if (this.closed) throw new Error('OnlineEngine: closed');
        const hit = await this.catchUpLocked(dedupKey);
        if (hit !== undefined) return { version: hit.version };
        this.assertWritable();
        if (reDerive) {
          // The catch-up folded any sibling-tab writes (they share our client_id, so they came
          // back as own events); resumeFromSeq covers the trimmed-stream case, where the seq
          // sits below the local floor and the stream alone under-derives.
          clientSeq = Math.max(this.record.lastSentClientSeq + 1, resumeFromSeq + 1);
          reDerive = false;
        }
      }

      // Skipped entirely for an occ:false event, and the skip can never hide a check:
      // defineAggregate rejects an event declaring both, because without
      // expected_aggregate_version the state can move between the check and the commit.
      if (useOcc && !checkPrecondition(this.def, this.notifier.value, { kind, payload })) {
        throw new ValidationError(kind);
      }

      const plaintext = JSON.stringify({ kind, payload });
      const event: WireEvent = {
        client_seq: clientSeq,
        event_seq: 0,
        event_timestamp: this.now(),
        event_type_major: wireType.major,
        event_type_minor: wireType.minor,
        event_value: plaintext,
        event_id: eventId,
      };
      // A fresh iv per ATTEMPT: a 409 retry re-encrypts and never resends an iv. The version
      // guard is on the envelope, so the server arbitrates conflicts over ciphertext.
      if (this.encryptsKind(kind)) {
        assert(this.crypto !== undefined, 'executeLocked: an encrypting kind has a provider');
        const sealed = await this.crypto.encryptEvent(this.type, this.id, plaintext);
        event.event_value = sealed.value;
        event.iv = sealed.iv;
      }
      const expectedVersion = this.record.serverVersion;

      try {
        const result = await this.gateway.write([event], {
          // "Nothing folded yet" is a statement about our cursor, not about the guard, so this
          // derivation is the same on both paths.
          createIfNotExist: expectedVersion === 0,
          // Omitted, not undefined-valued, for occ:false: the request carries no guard at all.
          ...(useOcc ? { expectedAggregateVersion: expectedVersion } : {}),
        });
        // The freshest hint wins outright: an unhinted response clears the pacing a hinted one
        // set. Only the success path carries a hint; an error's own Retry-After is the ladder's.
        this.writeNotBefore = pacedUntil(this.now(), result.writeBackoffMs);
        await this.onWriteSucceeded(kind, payload, event, result.aggregateVersion, expectedVersion, dedupKey, useOcc);
        return { version: result.aggregateVersion };
      } catch (e: unknown) {
        if (e instanceof Conflict) {
          // Our read was stale and the write was definitively rejected, so re-deriving is safe
          // (holding a seq silently drops the write on a conflict). An occ:false write cannot
          // legitimately reach here; if a gateway 409s one anyway, re-derive-and-retry is still
          // the right response.
          reDerive = true;
          nextDelayMs = backoffMs(attempt, this.now);
          continue;
        }
        if (e instanceof IdempotencyConflict) {
          // OCC passed but a SIBLING consumed the seq (the gateway answers 200 when it is ours,
          // so this is never our own landed write). Re-derive above the contested seq.
          reDerive = true;
          if (e.lastClientSeq !== undefined) resumeFromSeq = Math.max(resumeFromSeq, e.lastClientSeq);
          nextDelayMs = backoffMs(attempt, this.now);
          continue;
        }
        if (e instanceof Unavailable) {
          // Ambiguous: the write MIGHT have landed. HOLD the seq so a retry, or the next
          // catch-up's dedup hit, catches it. Honour Retry-After.
          nextDelayMs = e.retryAfterMs ?? backoffMs(attempt, this.now);
          continue;
        }
        if (e instanceof NotExist || e instanceof Forbidden || e instanceof Unauthorized) throw e;
        // Transport throw or timeout: ambiguous, the ack may have been lost. HOLD the seq; the
        // next attempt's catch-up may reveal the landed event, or the held-seq rewrite hits the
        // server's idempotency check.
        nextDelayMs = backoffMs(attempt, this.now);
      }
    }

    // Exhausted: one more catch-up so the version on the error is current truth.
    await this.catchUpLocked(dedupKey);
    throw new ConflictError(this.type, this.id, this.record.serverVersion);
  }

  // OCC guarantees version === expected + 1 on a genuine accept; an idempotency hit returned as
  // 200 can carry an EARLIER version (the original write's), so the cursor advances only when
  // gap-free and never regresses.
  private async onWriteSucceeded(
    kind: string, payload: unknown, event: WireEvent, version: number, expectedVersion: number,
    dedupKey: string, useOcc: boolean,
  ): Promise<void> {
    if (this.closed) return;
    const now = this.now();
    // Written in the same transaction as the seq or cursor advance below, so the index and the
    // record agree whatever happens between them.
    const dedup: DedupRecord = { type: this.type, id: this.id, eventId: dedupKey, version, expiresAt: now + DEDUP_WINDOW_MS };

    if (!useOcc) {
      // The version is the slot this batch committed at: a real fact about the server, and
      // still not a cursor we may take. The cursor rule (WIRE-CONTRACT, write route) allows one
      // exception, an OCC response where version === expected + 1, because the guard makes a
      // gap impossible. We sent no guard, so the equality proves nothing about what else
      // occupies the window: a peer batch can sit at or below our version unfolded, and moving
      // the cursor past it loses it silently. So: no fold, no cursor move.
      //
      // What does move is the seq, because the server accepted a batch carrying it, and the
      // dedup record above. The event reaches `state` on the next read, alongside every peer
      // that shared the window.
      await this.advanceSeqLocked(event.client_seq, now, dedup);
      return;
    }

    if (version === expectedVersion + 1) {
      await this.foldOwnWriteLocked(kind, payload, event, version, now, dedup);
      return;
    }
    // An idempotency hit, or an unexpected non-gap-free version: the event is already folded, or
    // a catch-up will fold it. Do not fold and do not regress.
    await this.advanceSeqLocked(event.client_seq, now, dedup);
    if (version > this.record.serverVersion) await this.catchUpLocked(dedupKey);
  }

  private async advanceSeqLocked(clientSeq: number, now: number, dedup: DedupRecord): Promise<void> {
    const updated = await this.store.unitOfWork(this.type, this.id, 'readwrite', async (uow) => {
      const record = this.mergeRecord(await uow.getRecord());
      await uow.pruneDedup(now);
      await uow.putDedup(dedup);
      const next = this.withSeq(record, clientSeq, now);
      await uow.putRecord(next);
      return next;
    });
    this.record = updated;
    this.status.emit();
  }

  // The own event is appended to the log at the version the server gave it, folded once, and the
  // cursor moves with it. The log holds PLAINTEXT: encryption is a wire concern.
  private async foldOwnWriteLocked(kind: string, payload: unknown, event: WireEvent, version: number, now: number, dedup: DedupRecord): Promise<void> {
    const row: NewEvent = {
      type: this.type, id: this.id, si: version, clientSeq: event.client_seq,
      clientId: this.clientId, userId: this.userId,
      ts: event.event_timestamp, serverTs: 0,
      typeMajor: event.event_type_major, typeMinor: event.event_type_minor,
      value: JSON.stringify({ kind, payload }),
    };
    const before = this.notifier.value;
    const outcome = await this.store.unitOfWork(this.type, this.id, 'readwrite', async (uow) => {
      const record = this.mergeRecord(await uow.getRecord());
      // A sibling tab may have folded ahead of this one; its rows sit in the tail and fold on
      // the same draft as the own event, so the unit of work is still one produce.
      const tail = await uow.readTail(record.appliedLi);
      const stored = await uow.append([row], record.lastLi + 1);
      await uow.pruneDedup(now);
      await uow.putDedup(dedup);
      const folded = foldEvents(this.def, before, [...tail, ...stored], true);
      const lastLi = lastLiOf(stored, lastLiOf(tail, record.appliedLi));
      if (folded.state !== before) {
        await uow.putState({ type: this.type, id: this.id, schemaVersion: this.def.schemaVersion, appliedLi: lastLi, state: folded.state });
      }
      const next: AggregateRecord = {
        ...this.withSeq(record, event.client_seq, now),
        serverVersion: Math.max(record.serverVersion, version),
        lastLi: Math.max(record.lastLi, lastLi),
        appliedLi: lastLi,
      };
      await uow.putRecord(next);
      return { record: next, state: folded.state, results: folded.results };
    });
    this.record = outcome.record;
    this.notifier.set(outcome.state, outcome.results);
    this.status.emit();
  }

  // Reads from the cursor plus one (own_events, so our own writes come back to be folded and to
  // seed the seq high-water), validates the whole read before persisting anything, then folds it
  // in one transaction and notifies once. Returns the dedup hit for `dedupKey` when it has
  // landed, in the index or folded this pass.
  private async catchUpLocked(dedupKey: string | undefined): Promise<DedupRecord | undefined> {
    if (this.closed) return undefined;
    if (dedupKey !== undefined) {
      const existing = await this.readDedup(dedupKey);
      if (existing !== undefined && existing.expiresAt > this.now()) return existing;
    }

    let batches: EventBatch[];
    try {
      batches = await this.gateway.read({ fromVersion: this.record.serverVersion + 1, ownEvents: true });
    } catch (e: unknown) {
      if (e instanceof NotExist) return undefined;  // not created yet: an empty projection
      throw e;
    }
    if (this.closed) return undefined;
    if (batches.length === 0) return undefined;

    const prepared = await this.prepareRead(batches);
    // One unfoldable event refuses the whole read: nothing is persisted, the cursor is unmoved,
    // and the aggregate latches read-only.
    if (prepared === undefined) return undefined;

    const now = this.now();
    await this.foldReadLocked(prepared, now);

    if (dedupKey !== undefined) {
      const hit = await this.readDedup(dedupKey);
      if (hit !== undefined && hit.expiresAt > this.now()) return hit;
    }
    return undefined;
  }

  private readDedup(dedupKey: string): Promise<DedupRecord | undefined> {
    return this.store.unitOfWork(this.type, this.id, 'readonly', (uow) => uow.getDedup(dedupKey));
  }

  // Decrypts and validates outside the transaction. Returns undefined when any event cannot be
  // folded by this build, having latched update-required.
  private async prepareRead(batches: EventBatch[]): Promise<PreparedRead | undefined> {
    assert(batches.length <= BATCHES_PER_CATCHUP_MAX, `prepareRead: ${batches.length} batches exceeds ${BATCHES_PER_CATCHUP_MAX}`);
    let eventCount = 0;
    for (const batch of batches) eventCount += batch.events.length;
    assert(eventCount <= EVENTS_PER_CATCHUP_MAX, `prepareRead: ${eventCount} events exceeds ${EVENTS_PER_CATCHUP_MAX}`);
    const rows: NewEvent[] = [];
    const dedupPuts: DedupRecord[] = [];
    const now = this.now();
    const tipTs = batches[batches.length - 1]!.serverTimestamp;
    let cursor = this.record.serverVersion;
    let maxOwnClientSeq = this.record.lastSentClientSeq;
    for (const batch of batches) {
      if (batch.aggregateVersion <= cursor) continue;
      cursor = batch.aggregateVersion;
      const ageMs = Math.max(0, tipTs - batch.serverTimestamp);
      const isOwn = batch.clientId === this.clientId;
      for (const ev of batch.events) {
        let value = ev.event_value;
        if (ev.iv !== undefined) {
          if (this.crypto === undefined) {
            this.markUpdateRequired(`refusing event with iv for unencrypted "${this.type}/${this.id}"`);
            return undefined;
          }
          value = await this.crypto.decryptEvent(this.type, this.id, ev.iv, ev.event_value);
        }
        if (!validateIncomingEvent(this.def, value, ev.event_type_minor)) {
          this.markUpdateRequired('refusing an unfoldable event (unknown kind, newer minor, or schema mismatch)');
          return undefined;
        }
        const kind = (JSON.parse(value) as { kind: string }).kind;
        if (this.encryptsKind(kind) && ev.iv === undefined) {
          this.markUpdateRequired(`refusing a plaintext event of encrypted kind "${kind}" (downgrade attack?)`);
          return undefined;
        }
        if (isOwn && ev.client_seq > maxOwnClientSeq) maxOwnClientSeq = ev.client_seq;
        rows.push({
          type: this.type, id: this.id, si: batch.aggregateVersion, clientSeq: ev.client_seq,
          clientId: batch.clientId, userId: batch.userId, ts: ev.event_timestamp, serverTs: batch.serverTimestamp,
          typeMajor: ev.event_type_major, typeMinor: ev.event_type_minor, value,
        });
        // Indexed with the REMAINING lifetime: the window minus the batch's server-time age, so
        // clock skew cannot silently disable indexing.
        if (ev.event_id !== undefined && ageMs < DEDUP_WINDOW_MS) {
          dedupPuts.push({ type: this.type, id: this.id, eventId: ev.event_id, version: batch.aggregateVersion, expiresAt: now + (DEDUP_WINDOW_MS - ageMs) });
        }
      }
    }
    assert(cursor >= this.record.serverVersion, 'prepareRead: the cursor never moves back');
    assert(rows.every((r) => r.si > this.record.serverVersion), 'prepareRead: every row is above the cursor');
    return { rows, dedupPuts, maxVersion: cursor, maxOwnClientSeq };
  }

  private async foldReadLocked(prepared: PreparedRead, now: number): Promise<void> {
    const before = this.notifier.value;
    const outcome = await this.store.unitOfWork(this.type, this.id, 'readwrite', async (uow) => {
      const record = this.mergeRecord(await uow.getRecord());
      // The cursor read inside this transaction carries a sibling tab's progress too, so a batch
      // it already appended is filtered here and its rows arrive through the tail instead. Tail
      // and new rows fold on one draft: one produce per unit of work.
      const tail = await uow.readTail(record.appliedLi);
      const rows = prepared.rows.filter((r) => r.si > record.serverVersion);
      const stored = await uow.append(rows, record.lastLi + 1);
      assert(stored.length === rows.length, 'foldReadLocked: one row appended per prepared row');
      // Prune then put: an entry that survives the prune is one this pass meant to keep, and an
      // entry written here is never removed by its own prune.
      await uow.pruneDedup(now);
      for (const put of prepared.dedupPuts) await uow.putDedup(put);
      const folded = foldEvents(this.def, before, [...tail, ...stored], true);
      const lastLi = lastLiOf(stored, lastLiOf(tail, record.appliedLi));
      if (folded.state !== before) {
        await uow.putState({ type: this.type, id: this.id, schemaVersion: this.def.schemaVersion, appliedLi: lastLi, state: folded.state });
      }
      const next: AggregateRecord = {
        ...this.withSeq(record, prepared.maxOwnClientSeq, now),
        serverVersion: Math.max(record.serverVersion, prepared.maxVersion),
        lastLi: Math.max(record.lastLi, lastLi),
        appliedLi: lastLi,
      };
      await uow.putRecord(next);
      return { record: next, state: folded.state, results: folded.results };
    });
    this.record = outcome.record;
    this.notifier.set(outcome.state, outcome.results);
    this.status.emit();
  }

  // The persisted state is at a schema this build does not project. Online state is a projection
  // of the server's stream and nothing local is authoritative, so the replica goes and the next
  // read starts from version 0. The seq counters survive: they describe what this client_id has
  // already spent, which a schema change does not undo.
  private async dropReplicaLocked(): Promise<void> {
    const carried = this.record;
    await this.store.unitOfWork(this.type, this.id, 'readwrite', async (uow) => {
      await uow.purge();
      const next: AggregateRecord = {
        ...this.freshRecord(),
        lastSentClientSeq: carried.lastSentClientSeq,
        nextClientSeq: Math.max(carried.nextClientSeq, carried.lastSentClientSeq),
        modifiedAt: this.now(),
      };
      await uow.putRecord(next);
      this.record = next;
    });
    assert(this.record.serverVersion === 0, 'dropReplicaLocked: the cursor restarts at version 0');
    assert(this.record.appliedLi === 0, 'dropReplicaLocked: nothing is folded into the fresh state');
    this.notifier.reset(initialFrozenState(this.def));
  }

  // A stored kind this build no longer knows latches update-required instead of bricking open();
  // the state stays where the fold got to.
  private foldOrLatch(base: TState, tail: ReadonlyArray<StoredEvent>): TState {
    try {
      return foldEvents(this.def, base, tail, false).state;
    } catch (e: unknown) {
      if (e instanceof UnknownEventKindError) {
        this.markUpdateRequired(e.message);
        return base;
      }
      throw e;
    }
  }

  // The persisted record carries the truth for the counters and the cursor (a sibling tab may
  // have moved them); appliedLi stays this tab's, because it describes this tab's state.
  private mergeRecord(persisted: AggregateRecord | undefined): AggregateRecord {
    if (persisted === undefined) return this.record;
    return {
      ...persisted,
      schemaVersion: this.def.schemaVersion,
      serverVersion: Math.max(persisted.serverVersion, this.record.serverVersion),
      nextClientSeq: Math.max(persisted.nextClientSeq, this.record.nextClientSeq),
      lastSentClientSeq: Math.max(persisted.lastSentClientSeq, this.record.lastSentClientSeq),
      // li is allocated from the persisted high-water, so a sibling tab's rows are never
      // overwritten by this tab's next append.
      lastLi: Math.max(persisted.lastLi, this.record.lastLi),
      appliedLi: this.record.appliedLi,
    };
  }

  private withSeq(record: AggregateRecord, clientSeq: number, now: number): AggregateRecord {
    const lastSent = Math.max(record.lastSentClientSeq, clientSeq);
    return {
      ...record,
      lastSentClientSeq: lastSent,
      nextClientSeq: Math.max(record.nextClientSeq, lastSent),
      createdAt: record.createdAt === 0 ? now : record.createdAt,
      modifiedAt: now,
    };
  }

  private freshRecord(): AggregateRecord {
    return {
      type: this.type, id: this.id,
      schemaVersion: this.def.schemaVersion,
      serverVersion: 0, lastSentClientSeq: 0, nextClientSeq: 0,
      lastLi: 0, appliedLi: 0, trimFloor: 1,
      createdAt: 0, modifiedAt: 0,
    };
  }

  private encryptsKind(kind: string): boolean {
    return lookupKind(this.def.encryptKinds, kind) ?? this.def.encrypt;
  }

  private encryptsSomething(): boolean {
    return this.def.encrypt || Object.values(this.def.encryptKinds).some((flag) => flag);
  }

  private assertWritable(): void {
    if (this.updateRequiredFlag) throw new UpdateRequiredError(this.type, this.id);
  }

  private markUpdateRequired(reason: string): void {
    console.error(`OnlineEngine "${this.type}/${this.id}": ${reason}`);
    if (this.updateRequiredFlag) { this.status.emit(); return; }
    this.updateRequiredFlag = true;
    this.onUpdateRequired?.();
    this.status.emit();
  }

  private enterSync(): void { this.inFlight += 1; this.status.emit(); }
  private exitSync(): void { this.inFlight -= 1; assert(this.inFlight >= 0, 'exitSync: balanced with enterSync'); this.status.emit(); }

  // Two tabs share one IDB keypair, so one wire client_id, so the sibling-seq race is real. Web
  // Locks serialise it across tabs; where the API is absent (Node, tests) a single instance runs
  // and the in-process mutex already serialises it.
  private async withLock<T>(fn: () => Promise<T>): Promise<T> {
    const locks = (globalThis as { navigator?: { locks?: { request?: unknown } } }).navigator?.locks;
    if (locks === undefined || typeof locks.request !== 'function') return fn();
    return (locks.request as (name: string, cb: () => Promise<T>) => Promise<T>)(`celeriant:execute:${this.type}\0${this.id}`, fn);
  }
}

function lastLiOf(rows: ReadonlyArray<StoredEvent>, fallback: number): number {
  return rows.length === 0 ? fallback : rows[rows.length - 1]!.li;
}

// 100ms * 2^(attempt-1) plus jitter under 50ms. The jitter comes off the clock rather than
// Math.random: sub-millisecond wall-clock entropy is enough to decorrelate retries.
function backoffMs(attempt: number, now: () => number = Date.now): number {
  return 100 * (1 << (attempt - 1)) + (now() % 50);
}

async function sleep(ms: number): Promise<void> {
  if (ms <= 0) return;
  await new Promise((r) => { setTimeout(r, ms); });
}

function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
